import { cn } from 'utils/cn';

const FOCUS_RING = 'outline-none focus-visible:ring-3 focus-visible:ring-ring/50';

/**
 * A top-level sidebar row: a `NavCollapse`, or a `NavItem` placed straight in a group. In the open
 * drawer it spans the drawer and draws hover, focus and `pill` as a rounded pill inset 8px under its
 * content; in the rail it is itself the pill around the icon, inset 8px each side (32px at rest), so the
 * icons stay centred and slide in with the logo while the column's width animates.
 */
export function topLevelRowClass(drawerOpen: boolean, pill: boolean): string {
    return drawerOpen
        ? cn(
              'focus-visible:before:ring-ring/50 relative isolate flex w-full cursor-pointer items-center py-2 pr-4 text-left outline-none before:absolute before:inset-x-2 before:inset-y-0 before:-z-10 before:rounded-lg focus-visible:before:ring-3',
              pill ? 'before:bg-sidebar-accent' : 'hover:before:bg-sidebar-hover focus-visible:before:bg-sidebar-hover'
          )
        : cn(
              'mx-2 flex w-[calc(100%-1rem)] cursor-pointer items-center justify-center rounded-md py-0.5',
              FOCUS_RING,
              pill ? 'bg-sidebar-accent' : 'hover:bg-sidebar-hover'
          );
}

/** The icon slot of a top-level row: 24px wide beside the label, a 36px box centred in the rail. */
export function topLevelIconClass(drawerOpen: boolean): string {
    return cn('text-primary inline-flex min-w-6 shrink-0', !drawerOpen && 'size-9 items-center justify-center');
}

/** The mark after the label of a row that opens in a new tab: 14px, muted like the group chevron. */
export const newTabIconClass = 'text-primary ml-1 size-3.5 opacity-60';

/** A nested row inside the open drawer: its own 8px-inset pill, indented to where the parent label starts. */
export function nestedRowClass(selected: boolean): string {
    return cn(
        'mx-2 flex items-center rounded-lg pr-4 pl-10',
        FOCUS_RING,
        selected ? 'bg-sidebar-accent' : 'hover:bg-sidebar-hover'
    );
}

/** A row of the flyout the rail opens beside a group: full width, bold on hover and when selected. */
export function popupRowClass(selected: boolean): string {
    return cn(
        'flex items-center px-4 py-[7px]',
        FOCUS_RING,
        'focus-visible:ring-inset',
        selected
            ? 'new:bg-accent bg-black/3 font-semibold'
            : 'hover:bg-sidebar-hover focus-visible:bg-sidebar-hover hover:font-semibold'
    );
}
